import React, { useMemo } from 'react'
import styled from 'styled-components'
import {
  Container as MuiContainer,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Card,
  CardContent,
  Grid,
} from '@material-ui/core'
import Divider from '@material-ui/core/Divider'
import _ from 'lodash'
import { makeStyles } from '@material-ui/core/styles'
import moment from 'moment'
import KsLogo from '../../../assets/ks-logo.png'
import { getWipDetailedReportDoc } from '../../../entities/project/service'
import formatCurrency from '../../../utils/formatCurrency'
import deepOmitObjectProperties from '../../../utils/deepOmitObjectProperty'
import SummaryFinancialsTable from './SummaryFinancialsTable'
import KeyDatesTable from './KeyDatesTable'
import { markdownSummaryToHtml } from './WipOrationEditor'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#A2A2FF',
    color: 'black',
    borderRadius: 12,
    minWidth: 250,
    textAlign: 'center',
  },
  label: {
    fontSize: 16,
  },
  value: {
    fontSize: 20,
    marginTop: theme.spacing(1),
  },
  summaryMarkdown: {
    marginLeft: 20,
    marginRight: 40,
    marginBottom: 20,
    fontSize: '1rem',
    lineHeight: 1.65,
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: '0.5em',
      marginBottom: '0.4em',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    '& h1': { fontSize: '1.25rem' },
    '& h2': { fontSize: '1.15rem' },
    '& h3': { fontSize: '1.05rem' },
    '& ul, & ol': {
      paddingLeft: '1.25rem',
      margin: '0.5em 0',
    },
    '& li': { margin: '0.25em 0' },
    '& p': {
      margin: '0 0 0.75em',
    },
    '& p:last-child': { marginBottom: 0 },
    '& strong': { fontWeight: 600 },
  },
}))

const StyledContainer = styled(MuiContainer)`
  background: #fff;
  padding: 20px;
  border: 1px solid #ddd;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 2px solid #ddd;
`

const Title = styled(Typography)`
  background-color: ${props => props.theme.colors.paper};
  padding: 5px 10px;
  border-radius: 5px;
`

const Section = styled.section`
  margin-top: 20px;
`

const StyledButton = styled(Button)`
  background-color: ${props => props.theme.colors.nightshade.medium};
  color: ${props => props.theme.colors.paper};

  &:hover {
    background-color: ${props => props.theme.colors.nightshade.dark};
  }

  border-radius: 40px;
`
const downloadDocx = (base64String, fileName = 'wip-report.docx') => {
  // Convert base64 to a byte array
  const byteCharacters = atob(base64String)
  const byteNumbers = new Array(byteCharacters.length)
    .fill(0)
    .map((_, i) => byteCharacters.charCodeAt(i))

  // Convert byte array to a Blob
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  })

  // Create a downloadable URL
  const blobUrl = URL.createObjectURL(blob)

  // Create an invisible anchor element and trigger the download
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()

  // Cleanup: Remove the anchor tag and revoke the URL
  document.body.removeChild(a)
  URL.revokeObjectURL(blobUrl)
}

const WorkInProgressReportPreview = ({
  project,
  month,
  person,
  wipData = {},
  client,
  invoiceTotals = [],
  oration,
  monthlyWipReports,
  currency,
  pacing,
  keyDates,
  budget,
  budgetTrend,
}) => {
  const weeklySummaryData = [
    {
      label: 'Staff Fees',
      value: formatCurrency(wipData.submittedHours.staffRate, project.CurrencyIsoCode),
    },
  ]

  const expertFeesData = [{ label: 'Expert Fees', value: wipData.submittedHours.expertRate }]
  const expensesData = [
    {
      label: 'Total Expenses(Costs & Disbursements)',
      value: wipData.submittedExpenses.staffExpenses + wipData.submittedHours.staffRate,
      isBold: true,
    },
  ]
  const classes = useStyles()

  const budgetMonths = useMemo(() => {
    const currentMonth = moment().startOf('month')
    return monthlyWipReports
      .filter(item => moment(item.month, 'YYYY-MM').isAfter(currentMonth))
      .map(item => {
        return {
          month: item.month,
          budget: item.budget,
        }
      })
      .slice(0, 7)
  }, [monthlyWipReports])

  const startOfMonth = moment()
    .startOf('month')
    .format('L')
  const currentDate = moment().format('L')

  const reportingPeriodDates = `${startOfMonth} - ${currentDate}`

  return (
    <StyledContainer maxWidth="md" data-testid="wip.previewPanel">
      <Header>
        <Title variant="h6">Value Delivery Review Report - Cumulative ({month})</Title>
        <StyledButton
          variant="contained"
          onClick={async () => {
            // Clean wipData by removing __typename, pacingWeek, and invoiceDiscount (not in WipDataInput schema)
            const cleanedWipData = deepOmitObjectProperties(wipData, [
              '__typename',
              'pacingWeek',
              'invoiceDiscount',
            ])
            // Clean monthlyWipReports for invoiceTotals - remove __typename, pacingWeek, and invoiceDiscount
            const cleanedInvoiceTotals = monthlyWipReports.map(w =>
              deepOmitObjectProperties(w, ['__typename', 'pacingWeek', 'invoiceDiscount']),
            )
            // Convert pacing to Float (it may be a string from wipPacing)
            const pacingFloat = typeof pacing === 'string' ? parseFloat(pacing) : pacing

            const wipReportDoc = await getWipDetailedReportDoc(
              client,
              _.omit(project, ['__typename']),
              cleanedWipData,
              keyDates,
              oration,
              budgetTrend,
              formatCurrency(wipData.totalFeesAndExpenses, currency),
              budgetMonths,
              pacingFloat,
              cleanedInvoiceTotals,
              person,
            )
            downloadDocx(wipReportDoc)
          }}
        >
          Download Report
        </StyledButton>
      </Header>
      <Section
        style={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <img
            alt="keystone logo"
            src={KsLogo}
            style={{
              width: '300px',
              height: 'auto',
            }}
          />
        </div>

        <div
          style={{
            width: '200px',
          }}
        >
          <Typography>
            <strong>Value Delivery Review Report</strong>
          </Typography>
          <Typography
            style={{
              fontStyle: 'italic',
              fontWeight: 200,
              marginTop: '10px',
            }}
          >
            Everything included in this report is considered draft until it is finalized and
            invoiced.
          </Typography>
        </div>
      </Section>

      <Section>
        <Typography variant="body1">
          <strong>Bill to:</strong> {project.client}
        </Typography>
        <Typography variant="body1">
          <strong>Attn:</strong> {person.fullName}
        </Typography>
        <Typography variant="body1">
          <strong>Address:</strong> {project.Address}
        </Typography>
      </Section>

      <Section
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <Typography>
          <strong>Summary</strong>
        </Typography>
        <Typography
          style={{ marginLeft: '20px', marginRight: '40px', marginBottom: '20px' }}
          component="div"
          className={classes.summaryMarkdown}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: markdownSummaryToHtml(
              oration || 'No summary context provided for this Value Delivery Review.',
            ),
          }}
        />
      </Section>

      <Section>
        <strong style={{ marginBottom: '20px' }}>Monthly Summary Financials</strong>

        <Grid
          style={{ marginTop: '20px', marginBottom: '20px' }}
          container
          spacing={4}
          justify="center"
        >
          <Grid item>
            <Card className={classes.root}>
              <CardContent>
                <Typography className={classes.label}>Budget Trend</Typography>
                <Typography className={classes.value}>{budgetTrend}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item>
            <Card className={classes.root}>
              <CardContent>
                <Typography className={classes.label}>Actuals to Date</Typography>
                <Typography className={classes.value}>
                  {formatCurrency(wipData.totalFeesAndExpenses, currency)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <SummaryFinancialsTable
          monthlyWipReports={monthlyWipReports}
          invoiceTotals={invoiceTotals}
          pacing={pacing}
          currency={currency}
          showCurrentMonthValue={false}
        />
      </Section>

      <Section>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Reporting Period</strong>
              </TableCell>
              <TableCell>
                <strong>Matter Name / Number</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{reportingPeriodDates}</TableCell>
              <TableCell>{project.name}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Section>
      <Section>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Current Staff Summary</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Line Total</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {weeklySummaryData.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  {item.isIndented ? (
                    <p style={{ marginLeft: '10px' }}>{item.label}</p>
                  ) : (
                    item.label
                  )}
                </TableCell>
                <TableCell align="right">
                  {item.isBold ? <strong>{item.value}</strong> : item.value}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <KeyDatesTable keyDates={keyDates} title="Projected Case Schedule" />

      {/*       <Section>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Experts</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expertFeesData.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  {item.isIndented ? (
                    <p style={{ marginLeft: '10px' }}>{item.label}</p>
                  ) : (
                    item.label
                  )}
                </TableCell>
                <TableCell align="right">
                  {item.isBold ? <strong>{item.value}</strong> : formatCurrency(item.value, project.CurrencyIsoCode)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
 */}
      <Section>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Expenses</strong>
              </TableCell>
              {/* <TableCell align="right">
                <strong>Line Total</strong>
              </TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {expensesData.map((item, index) => (
              <TableRow key={index}>
                <TableCell>
                  {item.isIndented ? (
                    <p style={{ marginLeft: '10px' }}>{item.label}</p>
                  ) : (
                    item.label
                  )}
                </TableCell>
                <TableCell align="right">
                  {item.isBold ? (
                    <strong>{formatCurrency(item.value, project.CurrencyIsoCode)}</strong>
                  ) : (
                    formatCurrency(item.value, project.CurrencyIsoCode)
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </StyledContainer>
  )
}

export default WorkInProgressReportPreview
